import { CommonModule } from '@angular/common';
import { Component, ElementRef, Host, OnDestroy, ViewChild } from '@angular/core';
import { IonContent, IonicModule, IonTextarea, NavController, ViewWillLeave } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';
import { EventService } from 'src/app/services/events/event.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { MediaService } from 'src/app/services/media-service/media.service';
import { CitationDetail, MediaHtmlResponse, WebSearchResultsDetail } from 'src/app/api/proxy/virtualHealthLibrary/virtual-health-library-models';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
import { sharedConfig } from 'src/environments/shared-config';
import { ActivatedRoute } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { LoadingService } from 'src/app/services/loading-service/loading.service';
import { VivaBotFaceState } from 'src/app/controls/viva-chat/viva-chat.component';

@Component({
  selector: 'app-media-chatbot-page',
  templateUrl: './media-chatbot.page.html',
  styleUrls: ['./media-chatbot.page.scss'],
  imports: [CommonModule, IonicModule, SharedModule]
})
export class MediaChatbotPage implements OnDestroy, ViewWillLeave {
  @ViewChild(IonContent, { static: false }) content?: IonContent;
  @ViewChild('bottomSentinel', { read: ElementRef }) bottomSentinel?: ElementRef<HTMLDivElement>;
  @ViewChild('inputContainer', { read: ElementRef }) inputContainer?: ElementRef<HTMLElement>;
  @ViewChild('content', { read: ElementRef }) contentHost?: ElementRef<HTMLElement>;
  @ViewChild('lastMessage', { read: ElementRef }) lastMessageElement?: ElementRef;
  @ViewChild('chatTextarea', { static: false }) chatTextarea?: IonTextarea;
  private _busyLoading: boolean = false;
  private subscriptions: Subscription[] = [];  // Store the subscription  
  private wait = (ms = 0) => new Promise(res => setTimeout(res, ms));
  public inputHeight = 0;
  private inputResizeObserver?: ResizeObserver;

  //mediaChatbotForm!: FormGroup;
  message: string = '';
  userMessages: string[] = [];
  chatMessages: IChatMessage[] = [];
  miniChatMessages: IChatMiniMessage[] = [];
  enableWebSearch: boolean = false;
  public static initialRequest?: IInitialQuestion;


  constructor(
    @Host() private hostRef: ElementRef<HTMLElement>,
    private _route: ActivatedRoute,
    public _navCtrl: NavController,
    private _translator: TranslatorService,
    protected _MediaService: MediaService
  ) {
    this.subscriptions.push(
      EventService.loadingEvent.subscribe(async (loading: number) => {
        this.busyLoading = loading > 0;
      }));
  }

  public get busyLoading(): boolean {
    return this._busyLoading;
  }

  public set busyLoading(value: boolean) {
    this._busyLoading = value;
    if (this._busyLoading) {
      EventService.aiBotEmotionChange.emit(VivaBotFaceState.thinking);
    } else {
      EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
    }

  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((a) => { a.unsubscribe(); })
    }
    this.stopAllAudio();
    this.inputResizeObserver?.disconnect();

  }

  async ionViewWillLeave() {
  document.body.classList.remove('has-sticky-chatbar');
 

    LoadingService.disableSilentLoading();

    await this.stopAllAudio();
  }

  async ionViewDidEnter() {
    document.body.classList.add('has-sticky-chatbar');

    EventService.aiBotEmotionChange.emit(VivaBotFaceState.happy);
    LoadingService.enableSilentLoading();

    await this.wait(0);
    this.setupFooterResizeObserver();
    if (!this.inputResizeObserver) {
      setTimeout(() => this.setupFooterResizeObserver(), 50);
    }

    if (MediaChatbotPage.initialRequest) {
      const params = await firstValueFrom(this._route.queryParamMap);
      const encodedUrl = params.get('src');
      
      if (encodedUrl) {
        const incomingRoute = decodeURIComponent(encodedUrl);
        const { dataContext, promptInstructions, formatInstructions, sourceRoute } = MediaChatbotPage.initialRequest;

        // Match routes - check if incoming route starts with source route
        const routeMatches = incomingRoute && sourceRoute && 
          (incomingRoute.toLowerCase().trim() === sourceRoute.toLowerCase().trim() ||
           incomingRoute.toLowerCase().startsWith(sourceRoute.toLowerCase()));

        if (routeMatches) {
          this.clear();
          
          // Store request before clearing (we still need it)
          const storedRequest = { ...MediaChatbotPage.initialRequest };
          MediaChatbotPage.initialRequest = undefined;

          // Combine the 3 parts: data + analysis instructions + detailed format
          const fullPrompt = this._translator.translateText('media-chat-bot-page.ai-prompt.full-chat-wrapper', {
            dataContext: storedRequest.dataContext,
            promptInstructions: storedRequest.promptInstructions,
            formatInstructions: storedRequest.formatInstructions,
          });

          // Show user-friendly message in chat
          const displayMessage: IChatMessage = { 
            text: this._translator.translateText('media-chat-bot-page.deeper-insight'), 
            sender: 'user', 
            isPlaying: false 
          };
          this.chatMessages.push(displayMessage);
          await this.scrollToBottomAccurate();

          // Process with the full detailed prompt
          await this.processMessage(fullPrompt);
        }
      }
    }
  }


  clear() {
    this.chatMessages = [];
    this.userMessages = [];
  }

  backAllowed(): boolean {
    return window.history.length > 1;
  }

  async goBack() {
    await this._navCtrl.back();
  }

  public async sendMessage() {
    if (this.message.trim() !== '') {
      // Push the user's message to the chatMessages array
      const msg: IChatMessage = { text: this.message, sender: 'user', isPlaying: false };
      this.chatMessages.push(msg);
      await this.scrollToBottomAccurate();

      setTimeout(async () => {
        await this.processMessage(this.message);
        this.message = ''; // Clear input
        await this.triggerHaptic();
        await this.scrollToBottomAccurate();


      }, 10);
    }
  }

  private async processMessage(message: string): Promise<void> {
    this.busyLoading = true;
    try {
      const response = await this._MediaService.aiQuery(message, this.enableWebSearch);
      let messageSet = false;

      if (response && response.responseText) {
        const msg: IChatMessage = {
          text: response.responseText,
          sender: 'bot',
          citation: response.citation,
          aiResponseId: response.id,
          webSearchResults: response.webSearchResultsDetails ?? [],
          isPlaying: false
        };

        if (msg && msg.text) {
          // Clean up any markdown artifacts or unwanted wrappers
          msg.text = msg.text
            .replace(/```[\w]*\n?/g, "")  // Remove code block markers
            .replace(/<body>/g, "")        // Remove <body> tags
            .replace(/<\/body>/g, "")
            .replace(/<html>/g, "")        // Remove <html> tags
            .replace(/<\/html>/g, "")
            .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")  // Convert markdown bold to HTML
            .trim();
        }

        this.chatMessages.push(msg);
        messageSet = true;
      }

      if (!messageSet) {
        let error = this._translator.translateText('media-chat-bot-page.bot-error');
        if (!response)
          error = this._translator.translateText('media-chat-bot-page.bot-no-answer');

        const msg2: IChatMessage = {
          text: error,
          sender: 'bot',
          isPlaying: false

        };

        this.chatMessages.push(msg2);
      }

    } finally {
      this.busyLoading = false;
      await this.scrollToBottomAccurate();
    }
  }

  // Function to handle viewing citations
  async viewCitations(chatMessage: IChatMessage): Promise<void> {
    const citation = chatMessage.citation as CitationDetail;

    if (citation && citation.mediaArticleId && !chatMessage.article) {

      chatMessage.article = await this._MediaService.getMediaArticle(citation.mediaArticleId);
      if (chatMessage.aiResponseId)
        await this._MediaService.aaiArticleReadAsync(chatMessage.aiResponseId);
    }
  }

  private setupFooterResizeObserver(): void {
    const footerEl = this.inputContainer?.nativeElement;
    if (!footerEl) {
      return;
    }

    const updateFooterOffset = () => {
      const height = Math.ceil(footerEl.getBoundingClientRect().height);
      if (height <= 0) {
        return;
      }

      this.inputHeight = height;
      this.hostRef.nativeElement.style.setProperty('--chat-footer-offset', `${height}px`);
    };

    this.inputResizeObserver?.disconnect();
    updateFooterOffset();
    this.inputResizeObserver = new ResizeObserver(updateFooterOffset);
    this.inputResizeObserver.observe(footerEl);
  }

  private async scrollToBottomAccurate(): Promise<void> {
    if (!this.content) {
      return;
    }

    await this.wait(0);

    const doScroll = async () => {
      const scrollEl = await this.content!.getScrollElement();
      const maxY = scrollEl.scrollHeight - scrollEl.clientHeight;
      await this.content!.scrollToPoint(0, Math.max(maxY, 0), 250);
    };

    await doScroll();
    requestAnimationFrame(() => void doScroll());
    setTimeout(() => void doScroll(), 120);
    setTimeout(() => void doScroll(), 300);
  }


  async togglePlay(audioPlayer: HTMLAudioElement, chatMessage: IChatMessage) {
    if (this.chatMessages) {
      this.chatMessages.forEach((cht) => {
        if (cht != chatMessage) {
          this.pauseAudio(cht);
        }
      })
    }

    chatMessage.audioPlayer = audioPlayer;
    if (!chatMessage.audioPlayer)
      return;
    chatMessage.isPlaying = !chatMessage.isPlaying;
    if (chatMessage.isPlaying) {
      await chatMessage.audioPlayer.play();
    } else {
      chatMessage.audioPlayer.pause();
    }
  }

  async thumbsUp(aiResponseId: string, thumbsUp: boolean) {
    await this._MediaService.aiResponseRelevantAsync(aiResponseId, thumbsUp);

    const target = this.chatMessages.find(m => m.aiResponseId === aiResponseId);
    if (target) {
      target.feedback = thumbsUp ? 'up' : 'down';
    }
  }

  updateTime(chatMessage: IChatMessage) {
    if (!chatMessage.audioPlayer)
      return;
    chatMessage.currentTime = chatMessage.audioPlayer.currentTime;
  }

  setAudioDuration(chatMessage: any, audioElement: HTMLAudioElement) {
    if (!audioElement)
      return;
    chatMessage.audioDuration = audioElement.duration;
    chatMessage.audioPlayer = audioElement;
  }
  seekAudio(chatMessage: IChatMessage, event: Event) {
    if (!chatMessage.audioPlayer)
      return;
    const newTime = (event.target as HTMLInputElement).value;
    chatMessage.audioPlayer.currentTime = +newTime;
  }

  formatTime(seconds?: number): string {
    if (seconds == undefined)
      seconds = 0;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }

  stopAudio(chatMessage: IChatMessage) {
    if (chatMessage.audioPlayer && chatMessage.isPlaying) {
      chatMessage.audioPlayer.pause();
      chatMessage.audioPlayer.currentTime = 0;  // Reset the playback position
      chatMessage.isPlaying = false;
    }
    this.stopSpeachAudio();
  }

  pauseAudio(chatMessage: IChatMessage) {
    if (chatMessage.audioPlayer && chatMessage.isPlaying) {
      chatMessage.audioPlayer.pause();
      chatMessage.isPlaying = false;
    }
  }

  stopAllAudio() {
    if (this.chatMessages) {
      this.chatMessages.forEach((cht) => {
        this.stopAudio(cht);
      })
    }

    this.stopSpeachAudio();

  }

  onEnter(e: any) {
    // skip IME composition (Chinese/Japanese input etc.)
    if (e.isComposing) return;

    // make sure it's the Enter key (redundant with (keydown.enter))
    if (e.key !== 'Enter') return;

    // allow Shift+Enter to insert newline
    if (e.shiftKey) return;

    if (this.busyLoading || !this.message.trim()) return;

    e.preventDefault();  // prevent newline
    this.sendMessage();
  }

  private async triggerHaptic() {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (err) {
        console.warn('Haptics not available', err);
      }
    }

  }


  async toggleSpeech(msg: IChatMessage) {

    if (msg.speechEnabled) {
      msg.speechEnabled = false;
    } else {
      msg.speechEnabled = true;
    }
    if (msg.speechEnabled) {
      setTimeout(async () => {
        await this.speakText(msg.text);

      }, 1);

    } else {
      await this.stopSpeachAudio();
    }

  }

  private stopSpeachAudio() {
   /* setTimeout(async () => {
      await TextToSpeech.stop(); // stop previous
    }, 10);*/
  }
  private async speakText(text: string) {
    return;
    /*
        if (!text) return;
    
        const lang = this.getDefaultBrowserLang();
    
        await TextToSpeech.stop(); // stop previous
    
         const plainText = this.htmlToSpeechText(text);
    
        await TextToSpeech.speak({
          text: plainText,
          lang,
          rate: 1.2,
          pitch: 1.0,
          volume:0.5,
          category: 'ambient',
          queueStrategy: QueueStrategy.Flush
        });*/
  }

  private htmlToSpeechText(html: string): string {
    if (!html) return '';

    // 1) insert line breaks where it makes sense
    let normalized = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n');

    // 2) let the browser parse & decode entities safely
    const tmp = document.createElement('div');
    tmp.innerHTML = normalized;

    // 3) extract text and tidy whitespace
    const text = (tmp.textContent || tmp.innerText || '')
      .replace(/\u00A0/g, ' ')     // nbsp -> space
      .replace(/[ \t]+\n/g, '\n')  // trim end-of-line spaces
      .replace(/\n{3,}/g, '\n\n')  // collapse big gaps
      .trim();

    return text;
  }

  private getDefaultBrowserLang(): string {
    return this._translator.getCulture();
  }

  async aiChatClicked() {
    if (this.busyLoading) return;            // avoid focusing while hidden by *ngIf
    await this.wait(0);                      // let change detection/render finish
    await this.chatTextarea?.setFocus();     // focuses the native <textarea>
  }

}

interface IChatMessage {
  speechEnabled?: boolean;
  text: string,
  sender: 'user' | 'bot',
  citation?: CitationDetail | null,
  article?: MediaHtmlResponse | null,
  audioPlayer?: HTMLAudioElement,
  isPlaying: boolean,
  audioDuration?: number,
  currentTime?: number,
  aiResponseId?: string,
  feedback?: 'up' | 'down' | null;
  webSearchResults?: WebSearchResultsDetail[];
}

interface IChatMiniMessage {
  text: string,
  sender: 'user' | 'bot',
  currentTime?: number,
  aiResponseId?: string,
}

export interface IInitialQuestion {
  dataContext: string,        // The actual metrics and user data
  promptInstructions: string, // How to analyze the data
  formatInstructions: string, // How to format the response (short vs detailed)
  sourceRoute: string
}